<template>
  <div class="cave-system-container">
    <!-- 未拥有洞府：创建引导层 -->
    <div 
          v-if="!hasCave && !caveLoading && showCreatePanel" 
          class="create-cave-overlay"
          @click.self="closeCreatePanel"
        >
      <div class="create-cave-panel">
         <!-- 添加关闭按钮 -->
      <div class="panel-close-btn" @click="closeCreatePanel">
        <Icon type="ios-close" size="24" />
      </div>
        <Icon type="ios-home" size="64" color="#2db7f5" />
        <h2>开辟洞府</h2>
        <p class="desc">消耗材料开辟属于自己的修真洞府，开启十二时辰修炼体系</p>

        <div class="material-checklist">
          <div
            v-for="(item, idx) in createRequirements"
            :key="idx"
            class="material-item"
            :class="{ 'sufficient': item.have >= item.need }"
          >
            <img :src="item.icon" v-if="item.icon" class="mat-icon">
            <div v-else class="mat-icon-placeholder">{{ item.name[0] }}</div>
            <div class="mat-info">
              <div class="mat-name">{{ item.name }}</div>
              <div class="mat-count" :class="{ 'enough': item.have >= item.need }">
                拥有: {{ item.have }} / 需要: {{ item.need }}
              </div>
            </div>
            <Icon
              :type="item.have >= item.need ? 'ios-checkmark-circle' : 'ios-close-circle'"
              :color="item.have >= item.need ? '#19be6b' : '#ed4014'"
              size="24"
            />
          </div>
        </div>

        <div class="cave-name-input">
          <Input
            v-model="newCaveName"
            placeholder="请输入洞府名称（2-6个字符）"
            :maxlength="6"
            style="width: 300px"
          />
        </div>

        <Button
          type="primary"
          size="large"
          :loading="isCreating"
          :disabled="!canCreate"
          @click="handleCreateCave"
          style="width: 300px; margin-top: 20px;"
        >
          {{ isCreating ? '开辟中...' : '立即开辟' }}
        </Button>
        <p v-if="!canCreate" class="warning-text">材料不足，无法开辟洞府</p>
      </div>
    </div>
    <!-- 加载中 -->
    <!-- 关闭后显示的重新打开按钮（放在 container 内合适位置） -->
    <div 
      v-if="!hasCave && !caveLoading && !showCreatePanel" 
      class="reopen-create-btn"
      @click="openCreatePanel"
    >
      <Button type="primary" size="large">
        <Icon type="ios-home" />
        开辟洞府
      </Button>
    </div>
    <div v-if="caveLoading" class="loading-container">
      <Spin size="large" fix>
        <Icon type="ios-loading" size=24 class="spin-icon-load"></Icon>
        <div>加载洞府数据中...</div>
      </Spin>
    </div>

    <div  v-if="hasCave && !caveLoading"  class="cave-system-layout">
    <!-- 左侧：洞府可视化与分配 -->
    <div v-if="!isMobile || !mobileDetailVisible" class="cave-visual-panel">
      <!-- 洞府头部信息 -->
      <div class="cave-header-bar">
        <div class="cave-title-section">
          <div class="cave-icon" :class="`level-${caveData.level}`">
            <Icon type="ios-home" size="28" />
          </div>
          <div>
            <h3>{{ caveData.caveName }}</h3>
            <div class="cave-meta">
              <Tag color="blue">Lv.{{ caveData.caveLevel }}</Tag>
              <span class="realm-text">{{ userData.realmName }}</span>
              <span class="exp-text">当前等级修为: {{ displayPlayerExp }} / {{ displayPlayerMaxExp }}</span>
            </div>
          </div>
        </div>
        <Button type="primary" size="small" @click="openCaveUpgrade" :loading="upgradingCave">
          <Icon type="ios-arrow-up" /> 升级洞府
        </Button>
      </div>

      <!-- 设施一览（含时辰分配） -->
      <div class="facility-overview">
        <div class="overview-header">
          <h4 class="overview-title">时辰分配</h4>
          <div class="slot-summary">
            <span class="used-slots">{{ usedSlots }}</span>
            <span class="separator">/</span>
            <span class="total-slots">12</span>
            <span class="label">时辰</span>
          </div>
        </div>

        <div class="facility-card-list">
          <div
            v-for="fac in facilities"
            :key="fac.id"
            class="facility-card-mini"
            :class="{ 'active': currentFacility === fac.id }"
            @click="enterFacility(fac.id)"
          >
            <div class="fac-card-top">
              <div class="fac-icon" :style="{ background: fac.color }">
                <Icon :type="fac.icon" size="20" color="#fff" />
              </div>
              <div class="fac-name">{{ fac.name }}</div>
              <div class="fac-level-tag">Lv.{{ fac.level }}</div>
            </div>

            <div class="fac-alloc-controls">
              <button
                class="alloc-btn minus"
                :disabled="fac.points <= 0"
                @click.stop="decreasePoints(fac.id)"
              >
                −
              </button>
              <div class="alloc-display">
                <span class="alloc-value">{{ fac.points }}</span>
                <span class="alloc-label">时辰</span>
              </div>
              <button
                class="alloc-btn plus"
                :disabled="usedSlots >= 12"
                @click.stop="increasePoints(fac.id)"
              >
                +
              </button>
            </div>

            <div class="fac-effect-text">
              <span v-if="fac.id === 'cultivation'">
                产出 {{ Math.floor(fac.baseOutput * (1 + fac.points * fac.perPointBonus)) }} 灵气/时辰
              </span>
              <span v-else>
                成功率 {{ getFacilitySuccessRateText(fac.id) }}
              </span>
              <span class="efficiency" v-if="fac.points > 0">
                (+{{ ((fac.points * fac.perPointBonus) * 100).toFixed(0) }}%)
              </span>
            </div>
          </div>
        </div>

        <div class="overview-footer">
          <div v-if="hasTimeSlotChanges" class="unsaved-hint">
            <Icon type="ios-alert" color="#ff9900" />
            <span>有未保存的修改</span>
          </div>
          <div v-else-if="usedSlots < 12" class="remaining-hint">
            <Icon type="ios-information-circle" />
            还有 {{ 12 - usedSlots }} 个时辰可分配
          </div>
          <div v-else class="full-hint">
            <Icon type="ios-checkmark-circle" color="#19be6b" />
            时辰已全部分配
          </div>
          <Button
            :type="hasTimeSlotChanges ? 'warning' : 'primary'"
            size="small"
            :loading="timeSlotSaving"
            @click="saveTimeSlotAllocation"
          >
            保存分配
          </Button>
        </div>
      </div>

    </div>

    <!-- 右侧：设施详情区域 -->
    <div v-if="!isMobile || mobileDetailVisible" class="cave-detail-area">
      <div v-if="isMobile && mobileDetailVisible" class="cave-mobile-back">
        <Button size="small" @click="backToFacilityList">
          <Icon type="ios-arrow-back" />
          返回列表
        </Button>
      </div>
      <!-- 默认欢迎页 -->
      <div v-show="!currentFacility" class="welcome-panel">
        <div class="welcome-content">
          <Icon type="ios-home" size="64" color="#2db7f5" />
          <h2>{{ caveData.caveName }}</h2>
          <p>当前境界：{{ caveData.realmName }} | 洞府等级：Lv.{{ caveData.caveLevel }}</p>
          <div class="stats-overview">
            <div class="stat-box">
              <div class="stat-num">{{ assignedSlots }}/12</div>
              <div class="stat-label">已分配时辰</div>
            </div>
            <div class="stat-box">
              <div class="stat-num">{{ Math.floor(caveData.spiritValue) }}</div>
              <div class="stat-label">当前灵气</div>
            </div>
            <div class="stat-box">
              <div class="stat-num">{{ caveData.totalOutput }}</div>
              <div class="stat-label">累计收取</div>
            </div>
          </div>
          <div class="cave-tips">
            <p>提示：在左侧设施卡片中点击 +/- 按钮分配时辰，点击卡片进入设施详情</p>
            <p>离线时灵气会自动积累，请记得及时收取 5分钟/次 计算一次不用时时看</p>
          </div>
        </div>
      </div>

      <!-- 修炼室 -->
      <div v-show="currentFacility === 'cultivation'" class="facility-panel">
        <CultivationRoom
          :level="getFacility('cultivation').level"
          :points="getFacilityPoints('cultivation')"
          :baseOutput="getFacility('cultivation').baseOutput"
          :perPointBonus="getFacility('cultivation').perPointBonus"
          :spiritValue="caveData.spiritValue"
          :playerLevel="playerLevel"
          :playerExp="playerExp"
          :playerMaxExp="playerMaxExp"
          :breachInfo="breachStuff"
          @upgrade="openFacilityUpgrade('cultivation')"
          @collected="onSpiritCollected"
          @breached="onBreakthrough"
        />
      </div>

      <!-- 炼丹房 -->
      <div v-show="currentFacility === 'alchemy'" class="facility-panel">
        <AlchemyRoom
          :level="getFacility('alchemy').level"
          :points="getFacilityPoints('alchemy')"
          :baseSuccessRate="Number(getFacilitySuccessRateValue('alchemy') * 100)"
          @upgrade="openFacilityUpgrade('alchemy')"
          @refined="onAlchemyRefined"
          @received="onAlchemyReceived"
        />
      </div>

      <!-- 炼器室 -->
      <div v-show="currentFacility === 'forging'" class="facility-panel">
        <ForgingRoom
          :level="getFacility('forging').level"
          :points="getFacilityPoints('forging')"
          :successBonus="(getFacilityPoints('forging') * 5)"
          @upgrade="openFacilityUpgrade('forging')"
          @forged="onForgingDone"
          @smelted="onSmeltDone"
          @treasureForged="onTreasureDone"
          @elementRotated="onElementDone"
          @sublimated="onSublimationDone"
          @received="onForgingReceived"
        />
      </div>
    </div>
    </div>

    <!-- 洞府升级弹窗 -->
      <Modal v-model="caveUpgradeVisible" title="洞府升级" width="500">
        <div class="cave-upgrade-content" v-if="upgradeInfo">
          <div class="upgrade-info">
            <h4>Lv.{{ caveData.caveLevel }} → Lv.{{ upgradeInfo.nextLevel }}</h4>
            <div class="upgrade-desc-box">
              {{ upgradeInfo.description || `设施等级上限提升至 Lv.${upgradeInfo.facilityLevelLimit}` }}
            </div>
          </div>
          <div class="upgrade-requirements">
            <div class="req-title">升级消耗：</div>
            <div class="req-exp" v-if="upgradeInfo.needExp">
              所需经验：{{ upgradeInfo.currentExp }} / {{ upgradeInfo.needExp }}
              <Progress :percent="(upgradeInfo.currentExp / upgradeInfo.needExp * 100)" :stroke-width="10" status="active" />
            </div>
            <div class="cost-list">
              <div class="cost-item" v-for="(cost, idx) in upgradeInfo.materials" :key="idx">
                <span>{{ cost.name }}</span>
                <span :class="{ 'insufficient': cost.have < cost.need }">
                    {{ cost.have }}/{{ cost.need }}
                  </span>
              </div>
            </div>
          </div>
        </div>
        <div slot="footer">
          <Button @click="caveUpgradeVisible = false">取消</Button>
          <Button type="primary" :loading="upgradingCave" :disabled="!canUpgradeCave" @click="handleUpgradeCave">
            确认升级
          </Button>
        </div>
      </Modal>
  <!-- 设施升级弹窗 -->
  <Modal v-model="facilityUpgradeVisible" title="设施升级" width="400">
    <div v-if="currentUpgradeFacility" class="facility-upgrade-content">
      <div class="facility-upgrade-header" :style="{ background: currentUpgradeFacility.color }">
        <Icon :type="currentUpgradeFacility.icon" size="48" color="#fff" />
        <h3>{{ currentUpgradeFacility.name }}</h3>
        <div class="level-text">Lv.{{ currentUpgradeFacility.level }} → Lv.{{ currentUpgradeFacility.level + 1 }}</div>
        <!-- 达到上限提示 -->
        <div v-if="!currentUpgradeFacility.canUpgrade && currentUpgradeFacility.level >= currentUpgradeFacility.maxLevel" class="limit-tip">
          已达洞府等级上限
        </div>
      </div>

      <!-- 升级效果 -->
      <div class="upgrade-effects">
        <h4>升级效果：</h4>
        <div class="upgrade-desc-box facility">
          {{ (facilityUpgradeConfig && facilityUpgradeConfig.description) || `设施等级提升至 Lv.${currentUpgradeFacility.targetLevel}` }}
        </div>
      </div>


      <!-- 升级材料 -->
      <div class="upgrade-materials" v-if="facilityUpgradeMaterials && facilityUpgradeMaterials.length > 0">
        <h4>升级材料：</h4>
        <div class="material-list">
          <div
            v-for="(mat, idx) in facilityUpgradeMaterials"
            :key="idx"
            class="material-row"
            :class="{ 'insufficient': mat.have < mat.need }"
          >
            <div class="mat-info">
              <span class="mat-name">{{ mat.name }}</span>
            </div>
            <span class="mat-count" :class="{ 'enough': mat.have >= mat.need, 'not-enough': mat.have < mat.need }">
            {{ mat.have || 0 }} / {{ mat.need }}
          </span>
          </div>
        </div>
        <!-- 材料不足提示 -->
        <div v-if="!materialEnough" class="material-warning">
          <Icon type="ios-alert" color="#ed4014" />
          材料不足，无法升级
        </div>
      </div>

      <!-- 无需材料 -->
      <div v-else-if="currentUpgradeFacility.canUpgrade" class="no-cost">
        <Icon type="ios-checkmark-circle" color="#19be6b" />
        该等级升级无需消耗材料，可直接升级
      </div>

      <!-- 已达上限 -->
      <div v-else-if="currentUpgradeFacility.level >= currentUpgradeFacility.maxLevel" class="no-cost limit">
        <Icon type="ios-lock" color="#999" />
        请先升级洞府以提升设施等级上限（当前上限 Lv.{{ currentUpgradeFacility.maxLevel }}）
      </div>
    </div>
    <div slot="footer">
      <Button @click="facilityUpgradeVisible = false">取消</Button>
      <Button
        type="primary"
        :loading="upgradingFacility === currentFacility"
        :disabled="!canUpgradeFacility"
        @click="handleFacilityUpgrade"
      >
        确认升级
      </Button>
    </div>
  </Modal>

  </div>
</template>

<script>
import CultivationRoom from './cave/CultivationRoom.vue';
import AlchemyRoom from './cave/AlchemyRoom.vue';
import ForgingRoom from './cave/ForgingRoom.vue';


export default {
  name: 'CaveSystem',
  // 添加props接收父组件数据
  props: {
    visible: Boolean,
    playerLevel: Number,
    playerExp: Number,
    playerMaxExp: Number
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.initData()
      }
    },
    currentFacility(value) {
      if (this.isMobile && value) {
        this.mobileDetailVisible = true
      }
    },
    breachStuff() {
      this.updateCustomCollectDefault()
    },
    'caveData.spiritValue'() {
      this.updateCustomCollectDefault()
    },
    playerExp() {
      this.updateCustomCollectDefault()
    },
    playerMaxExp() {
      this.updateCustomCollectDefault()
    },
  },
  data() {
    return {
      isMobile: false,
      mobileDetailVisible: false,
      // 状态管理
      hasCave: false,
      caveLoading: true,
      showCreatePanel: true,  // 控制创建面板显示
      isCreating: false,
      upgradingCave: false,
      isBreakingThrough: false,

      // 洞府基础数据
      caveData: {
        id: '',
        caveName: '',
        caveLevel: 1,
        currentExp: 0,
        spiritValue: 0,
        totalCollectedSpirit: 0,
        realmLevel: 1,
        realmName: '练气期',
        lastSpiritUpdate: new Date()
      },
      currentLevelConfig: {},

      // 创建洞府
      newCaveName: '青云洞府',
      createRequirements: [],

      // 十二时辰分配
      timeSlots: [],
      timeSlotSaving: false,
      hasTimeSlotChanges: false,

      // 设施数据
      facilities: [
        {
          id: 'cultivation',
          name: '修炼室',
          icon: 'ios-body',
          color: '#19be6b',
          level: 1,
          points: 0,
          baseOutput: 100,
          perPointBonus: 0.1
        },
        {
          id: 'alchemy',
          name: '炼丹房',
          icon: 'ios-flame',
          color: '#ff6b6b',
          level: 1,
          points: 0,
          baseOutput: 10,
          perPointBonus: 0.05
        },
        {
          id: 'forging',
          name: '炼器室',
          icon: 'ios-hammer',
          color: '#2db7f5',
          level: 1,
          points: 0,
          baseOutput: 5,
          perPointBonus: 0.05
        }
      ],
      currentFacility: null,

      // 升级相关
      caveUpgradeVisible: false,
      upgradeInfo: null,
      facilityUpgradeVisible: false,
      currentUpgradeFacility: null,
      materialEnough: false,
      facilityUpgradeConfig: null,
      facilityUpgradeMaterials: [],
      upgradingFacility: null,

      // 境界突破
      breachStuff: null,

      // 炼丹炼器
      forgeVisible: false,
      recipeList: [],
      availableRecipes: [],
      availableBlueprints: [],

      // 定时器
      spiritTimer: null,
      // 新增：洞府状态
      //createCaveVisible: false, // 创建弹窗
      materialList : [],
      // 创建洞府所需材料（可从后端获取，也可前端配置）
      /*caveCreateCost: [
        { name: '灵石', icon: 'ios-cash', need: 5000, have: 0, itemId: 'coin' },
        { name: '建府令', icon: 'ios-paper', need: 1, have: 0, itemId: 10001 },
        { name: '玄铁矿', icon: 'ios-cube', need: 10, have: 0, itemId: 10002 }
      ],*/
      // 修炼室数据
      cultivationData: {
        spirit: 1000, // 当前灵气
        totalCollected: 0
      },
      customCollectAmount: 0,
      // 炼丹相关 - 复用你原有数据结构
      //alchemyModalVisible: false,
      selectedRecipe: null,
      isRefining: false,
      userData: null,
      refiningProgress: 0,
      recipeSearch: '',
      auxiliaryNums: [],

      // 锻造相关 - 复用你原有数据结构
      forgingModalVisible: false,
      forgeTab: 'blueprints',
      blueprintSearch: '',
      selectedBlueprint: null,
      tuzhiingList1: [], // 委托列表

    }
  },
  components: { CultivationRoom, AlchemyRoom, ForgingRoom },
  computed: {
    assignedSlots() {
      return this.timeSlots.filter(s => s.facilityType).length
    },
    displayPlayerExp() {
      return Math.floor(this.playerExp || 0)
    },
    displayPlayerMaxExp() {
      return this.playerMaxExp || 0
    },
    totalCollected() {
      return this.caveData.totalCollectedSpirit || 0
    },
    usedSlots() {
      return this.facilities.reduce((sum, f) => sum + f.points, 0)
    },
    canCreate() {
      return this.createRequirements.every(item => item.have >= item.need) &&
        this.newCaveName.length >= 2 &&
        this.newCaveName.length <= 6
    },
    canUpgradeCave() {
      if (!this.upgradeInfo || !this.upgradeInfo.materials) return false
      return this.upgradeInfo.materials.every(m => m.have >= m.need) &&
        this.upgradeInfo.currentExp >= this.upgradeInfo.needExp
    },
    canUpgradeFacility() {
      if (!this.currentUpgradeFacility) return false
      if (this.facilityUpgradeMaterials.length === 0) return true
      return this.facilityUpgradeMaterials.every(m => m.have >= m.need)
    },
    expDiffNeeded() {
      return Math.max(0, this.displayPlayerMaxExp - this.displayPlayerExp)
    },
  },
  mounted() {
    this.checkMobile()
    // 不在这里调用 initData，由 watch visible 控制
    // 页面可见性变化时重新计算
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    window.addEventListener('resize', this.checkMobile)
  },

  beforeDestroy() {
    if (this.spiritTimer) clearInterval(this.spiritTimer)
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    window.removeEventListener('resize', this.checkMobile)
  },
  methods: {
    checkMobile() {
      const responsive = this.$store && this.$store.state && this.$store.state.responsive
        ? this.$store.state.responsive
        : null
      this.isMobile = responsive ? !!responsive.isMobile : window.innerWidth <= 768
      if (!this.isMobile) {
        this.mobileDetailVisible = false
      }
    },
    backToFacilityList() {
      this.mobileDetailVisible = false
    },
    // ========== 初始化 ==========
    async initData() {
      this.userData = JSON.parse(localStorage.getItem("userActivity")) || {};
      this.caveLoading = true
      this.hasTimeSlotChanges = false
      this.currentFacility = null
      this.mobileDetailVisible = false
      try {
        await this.checkCaveStatus()
      } finally {
        this.caveLoading = false
        this.updateCustomCollectDefault()
      }
    },
     // 关闭创建面板
    closeCreatePanel() {
      this.showCreatePanel = false
      this.$Message.info('可随时点击【开辟洞府】按钮重新打开')
    },
    
    // 重新打开创建面板
    openCreatePanel() {
      this.showCreatePanel = true
    },
    
    async checkCaveStatus() {
      try {
        const res = await this.$http.get(`/gameCave/info?charaId=${this.getCookie('charaId')}`)
        if (res.data.status === 200 && res.data.data) {
          this.hasCave = true
          this.parseCaveData(res.data.data)
          await this.loadBreachInfo()
        } else {
          this.hasCave = false
          await this.loadCreateRequirements()
        }
      } catch (err) {
        this.$Message.error('获取洞府信息失败')
        this.hasCave = false
      }
    },
    // ========== 数据解析 ==========
    parseCaveData(data) {
      // 主数据
      this.caveData = {
        ...this.caveData,
        ...data
      }

      // 解析设施数据
      if (data.facilities) {
        data.facilities.forEach(fac => {
          const target = this.facilities.find(f => f.id === fac.id)
          if (target) {
            target.level = fac.level
            target.points = fac.points || 0
            // 从后端配置读取实际数值，这里简化处理
            if (fac.level >= 1) {
              target.baseOutput =fac.baseOutput
              target.perPointBonus =fac.perPointBonus
            }
          }
        })
      }

      // 解析时辰分配
      if (data.timeSlots) {
        this.timeSlots = data.timeSlots.map((slot, index) => ({
          slotIndex: index,
          earthlyBranch: slot.branch || this.getEarthlyBranch(index),
          facilityType: slot.facility || null
        }))
      } else {
        // 初始化默认时辰
        this.initTimeSlots()
      }

      // 获取升级信息
      this.loadUpgradeInfo()
    },
    initTimeSlots() {
      const branches = ['子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥']
      this.timeSlots = branches.map((branch, index) => ({
        slotIndex: index,
        earthlyBranch: branch,
        facilityType: null
      }))
    },
    getEarthlyBranch(index) {
      const branches = ['子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥']
      return branches[index]
    },
    // ========== 创建洞府 ==========
    async loadCreateRequirements() {
      try {
        const res = await this.$http.get(`/gameCave/createRequirements?charaId=${this.getCookie('charaId')}`)
        if (res.data.status === 200) {
          this.createRequirements = res.data.data || []
        }
      } catch (err) {
        // 使用默认配置
        this.createRequirements = [
          { name: '灵石', itemId: 'coin', need: 5000, have: 0, icon: '' },
          { name: '建府令', itemId: '10001', need: 1, have: 0, icon: '' },
          { name: '玄铁矿', itemId: '10002', need: 10, have: 0, icon: '' }
        ]
      }
    },
    async handleCreateCave() {
      if (!this.canCreate) return
      this.isCreating = true
      try {
        const res = await this.$http.post(`/gameCave/create/?charaId=${this.getCookie('charaId')}&caveName=${this.newCaveName}`);

        if (res.data.status === 200) {
          this.$Message.success('洞府开辟成功！')
          this.parseCaveData(res.data.data)
          this.hasCave = true
        } else {
          this.$Message.error(res.data.msg || '创建失败')
        }
      } catch (err) {
        this.$Message.error(err.data.msg || '创建失败')
      } finally {
        this.isCreating = false
      }
    },
    // ========== 时辰分配 ==========
    updateFacilityPoints() {
      this.facilities.forEach(fac => {
        fac.points = this.timeSlots.filter(s => s.facilityType === fac.id).length
      })
    },

    increasePoints(facilityId) {
      if (this.usedSlots >= 12) return
      const fac = this.facilities.find(f => f.id === facilityId)
      if (fac) {
        fac.points++
        this.hasTimeSlotChanges = true
      }
    },
    decreasePoints(facilityId) {
      const fac = this.facilities.find(f => f.id === facilityId)
      if (fac && fac.points > 0) {
        fac.points--
        this.hasTimeSlotChanges = true
      }
    },
    async saveTimeSlotAllocation() {
      this.timeSlotSaving = true
      try {
        const slots = []
        let slotIndex = 0
        this.facilities.forEach(fac => {
          for (let i = 0; i < fac.points; i++) {
            slots.push({ index: slotIndex++, facility: fac.id })
          }
        })
        while (slots.length < 12) {
          slots.push({ index: slotIndex++, facility: null })
        }

        const res = await this.$http.post('/gameCave/assignTimeSlots', {
          charaId: this.getCookie('charaId'),
          slots: slots
        })

        if (res.data.status === 200) {
          this.$Message.success('时辰分配已保存')
          this.hasTimeSlotChanges = false
          this.updateFacilityPoints()
          this.checkCaveStatus()
        } else {
          this.$Message.error(res.data.msg)
        }
      } catch (err) {
        this.$Message.error((err.data && err.data.msg) || '保存失败')
      } finally {
        this.timeSlotSaving = false
      }
    },

    getFacilityPoints(facilityId) {
      return this.timeSlots.filter(s => s.facilityType === facilityId).length
    },

    getFacility(facilityId) {
      return this.facilities.find(f => f.id === facilityId) || {}
    },

    getPointBonus(facilityId) {
      const fac = this.getFacility(facilityId)
      return fac.perPointBonus || 0.1
    },
    getFacilitySuccessRateValue(facilityId) {
      const fac = this.getFacility(facilityId)
      const base = Number(fac.baseOutput || 0)
      const points = Number(fac.points || 0)
      const perPointBonus = Number(fac.perPointBonus || 0)
      return Math.max(0, Math.min(1, base + points * perPointBonus))
    },
    getFacilitySuccessRateText(facilityId) {
      return (this.getFacilitySuccessRateValue(facilityId) * 100).toFixed(0) + '%'
    },
    getAlchemyRecipePreviewRate(recipe) {
      const recipeBaseRate = Number(recipe && (recipe.baseRate != null ? recipe.baseRate : recipe.basicRate) || 0)
      const facility = this.getFacility('alchemy')
      const points = Number(facility.points || 0)
      const perPointBonus = Number(facility.perPointBonus || 0)
      const total = Math.max(0, Math.min(1, recipeBaseRate + points * perPointBonus))
      return (total * 100).toFixed(0) + '%'
    },
    // ========== 灵气收集 ==========
    /*calculateLocalSpiritGrowth() {
      // 前端简单的灵气增长计算（用于实时显示，实际以服务端为准）
      const cultivation = this.getFacility('cultivation')
      const points = cultivation.points
      if (points === 0) return

      const minutesPassed = 1 // 每分钟调用一次
      const hours = minutesPassed / 60
      const baseOutput = cultivation.baseOutput
      const perPointBonus = cultivation.perPointBonus

      const output = baseOutput * points * (1 + perPointBonus * points) * hours
      if (output > 0) {
        this.caveData.spiritValue += output
      }
    },*/

    async handleCollectSpirit(amount) {
      try {
        const res = await this.$http.post(`/gameCave/collectSpirit/?charaId=${this.getCookie('charaId')}&amount=${amount}`);
        if (res.data.status === 200) {
          this.$Message.success(res.data.data);
          this.$emit('exp-updated');
          this.$bus.$emit('caveCollectExp');
        } else {
          this.$Message.warning(res.data.msg);
        }
        await this.checkCaveStatus();
        await this.loadBreachInfo();
      } catch (err) {
        this.$Message.error(err.data.msg || '收取失败')
      }
    },

    async handleCustomCollect() {
      const maxCollectable = Math.floor(this.caveData.spiritValue || 0)
      const amount = Number(this.customCollectAmount)
      if (!Number.isInteger(amount) || amount <= 0) {
        this.$Message.warning('请输入大于0的整数收取数量')
        return
      }
      if (maxCollectable <= 0) {
        this.$Message.warning('当前暂无可收取灵气')
        return
      }
      if (amount > maxCollectable) {
        this.$Message.warning(`最多可收取 ${maxCollectable} 点灵气`)
        return
      }
      await this.handleCollectSpirit(amount)
    },

    updateCustomCollectDefault() {
      const spiritValue = Math.floor(this.caveData.spiritValue || 0)
      const expDiff = this.expDiffNeeded
      if (spiritValue === 0) {
        this.customCollectAmount = 0
      } else if (expDiff <= 0) {
        // 修为已满（准备突破或突破失败），允许收取全部灵气
        this.customCollectAmount = spiritValue
      } else if (spiritValue >= expDiff) {
        this.customCollectAmount = expDiff
      } else {
        this.customCollectAmount = spiritValue
      }
    },

    handleVisibilityChange() {
      if (!document.hidden) {
        // 页面重新可见时，重新获取数据以同步离线收益
        this.checkCaveStatus()
      }
    },

    // ========== 洞府升级 ==========
    async loadUpgradeInfo() {
      try {
        const res = await this.$http.get(`/gameCave/upgradeInfo?charaId=${this.getCookie('charaId')}`)
        if (res.data.status === 200) {
          this.upgradeInfo = res.data.data
        }
      } catch (err) {
        console.error('获取升级信息失败', err)
      }
    },


    // 新增：打开创建弹窗
    openCreateCave() {
      this.loadCreateRequirements() // 刷新材料数量
      this.createCaveVisible = true
    },
    // 新增：创建洞府
    async createCave() {
      // 检查材料是否足够
      const insufficient = this.caveCreateCost.filter(item => item.have < item.need)
      if (insufficient.length > 0) {
        this.$Message.warning(`材料不足：${insufficient.map(i => i.name).join('、')}`)
        return
      }

      this.isCreating = true
      try {
        const res = await this.$http.post('/gameCave/create', {
          charaId: this.getCookie('charaId'),
          name: '青云洞府' // 可让玩家输入，或使用默认
        })

        this.$Message.success('洞府创建成功！')
        this.createCaveVisible = false
        this.hasCave = true

        // 重新加载洞府数据
        this.caveData = res.data.data
        this.loadFacilityData()

      } catch (err) {
        this.$Message.error(err.data.msg || '创建失败')
      } finally {
        this.isCreating = false
      }
    },
    openCaveUpgrade() {
      this.loadUpgradeInfo()
      this.caveUpgradeVisible = true
    },

    async handleUpgradeCave() {
      this.upgradingCave = true
      try {
        // const res = await this.$http.post('/gameCave/upgrade', {
        //   charaId: this.getCookie('charaId')
        // })
        const res = await this.$http.post(`/gameCave/upgrade?charaId=${this.getCookie('charaId')}`)
        if (res.data.status === 200) {
          this.$Message.success('洞府升级成功！')
          this.parseCaveData(res.data.data)
          this.caveUpgradeVisible = false
        } else {
          this.$Message.error(res.data.msg)
        }
      } catch (err) {
        this.$Message.error(err.data.msg || '升级失败')
      } finally {
        this.upgradingCave = false
      }
    },

    // ========== 设施升级 ==========
    openFacilityUpgrade(facilityId) {
      const fac = this.getFacility(facilityId)
      this.currentUpgradeFacility = { ...fac, targetLevel: fac.level + 1 }
      this.loadFacilityUpgradeConfig(facilityId)
      this.facilityUpgradeVisible = true
    },
    async loadFacilityUpgradeConfig(facilityType, targetLevel) {
      try {
        this.facilityUpgradeConfig = null
        // 获取材料需求
        // 调用新的统一接口，不需要传 targetLevel，后台自动计算下一级
        const res = await this.$http.get('/gameCave/facilityUpgradeInfo', {
          params: {
            charaId: this.getCookie('charaId'),
            facilityType: facilityType  // cultivation/alchemy/forging
          }
        })
        if (res.data.status === 200) {
          const data = res.data.data
          this.currentUpgradeFacility = {
            id: data.facilityType,
            name: data.facilityName,
            level: data.currentLevel,
            targetLevel: data.nextLevel,
            maxLevel: data.maxLevel,
            canUpgrade: data.canUpgrade,
            color: this.getFacilityColor(facilityType),
            icon: this.getFacilityIcon(facilityType)
          }

          this.facilityUpgradeConfig = {
            outputBonus: data.outputBonus,
            pointBonus: data.successRateBonus,
            description: data.description || ''
          }

          this.facilityUpgradeMaterials = data.materials || [];
          this.canUpgradeFacility = data.canUpgrade;
          this.materialEnough =data.materialEnough;
        }
      } catch (err) {
        this.$Message.error('获取升级信息失败')
        this.facilityUpgradeMaterials = []
      }
    },

    async handleFacilityUpgrade() {
      if (!this.currentUpgradeFacility) return
      this.upgradingFacility = this.currentUpgradeFacility.id
      try {
        const res = await this.$http.post("/gameCave/upgradeFacility?charaId=" +
          this.getCookie("charaId") +
          "&facilityType=" + this.currentUpgradeFacility.id
        )
        if (res.data.status === 200) {
          this.$Message.success('设施升级成功！')
          const fac = this.facilities.find(f => f.id === this.currentUpgradeFacility.id)
          if (fac) fac.level++
          this.facilityUpgradeVisible = false
        } else {
          this.$Message.error(res.data.msg)
        }
      } catch (err) {
        this.$Message.error(err.data.msg || '升级失败')
      } finally {
        this.upgradingFacility = null
      }
    },
// ========== 境界突破 ==========
    async loadBreachInfo() {
      try {
        const res = await this.$http.post(`/gameRealm/getCaiRealm?charaId=${this.getCookie('charaId')}`)
        if (res.data.status === 200) {
          this.breachStuff = res.data.data
        }
      } catch (err) {
        console.error('获取境界信息失败', err)
      }
    },

    async handleBreakthrough() {
      this.isBreakingThrough = true
      try {
        const res = await this.$http.post(`/gameRealm/breakThroughTheRealm?charaId=${this.getCookie('charaId')}`)
        if (res.data.status === 200) {
          this.$Message.success(res.data.msg || '突破成功！')
        } else {
          this.$Message.warning(res.data.msg)
        }
      } catch (err) {
        this.$Message.warning(err.data.msg || '突破失败')
      } finally {
        this.$bus.$emit('caveBreakthrough');
        await this.checkCaveStatus()
        await this.loadBreachInfo()
        this.isBreakingThrough = false
        // 等父组件 props 更新后重新计算收取灵气默认值
        this.$nextTick(() => {
          this.updateCustomCollectDefault()
        })
      }
    },
    // 计算是否可创建（材料足够）  OLD 旧代码如下
    canCreateCave() {
      return this.caveCreateCost.every(item => item.have >= item.need)
    },

    // 原有方法保留...
    loadFacilityData() {
      // 加载设施数据、时辰分配等
      this.loadRecipeList()
      this.loadBlueprintList()
    },
    openforge() {
      this.forgeVisible = true;
    },
    loadRecipeList() {
      this.recipeList = this.$store.state.liandanList || []
    },

    loadBlueprintList() {
      // 复用你原有的加载图纸逻辑
      // this.$http.post(...)

    },
    getNodePosition(index) {
      const angle = (index * 30 - 90) * (Math.PI / 180)
      const radius = 120
      const center = 140
      const x = Math.cos(angle) * radius + center - 20
      const y = Math.sin(angle) * radius + center - 20
      return { left: `${x}px`, top: `${y}px` }
    },
    enterFacility(facilityId) {
      this.currentFacility = facilityId
      if (facilityId === 'cultivation') {
        this.checkCaveStatus()
        this.loadBreachInfo()
      }
      if (this.isMobile) {
        this.mobileDetailVisible = true
      }
    },
    showCaveStats() {
      this.currentFacility = null
      if (this.isMobile) {
        this.mobileDetailVisible = true
      }
    },
    calculateFacilityOutput(facilityId) {
      const points = this.getFacilityPoints(facilityId)
      const facility = this.facilities.find(f => f.id === facilityId)
      const level = facility.level || 1
      if (facilityId === 'cultivation') {
        return { spirit: points * (50 + level * 10), exp: points * (100 + level * 20) }
      }
      return {}
    },
    // 境界突破确定
    changeRealm() {
      this.$http
        .post(
          "/gameRealm/breakThroughTheRealm?charaId=" + this.getCookie("charaId")
        )
        .then((res) => {
          this.$Message.success(res.data.msg);
        })
        .catch((err) => {
          this.$Message.warning(err.data.msg);
        });
    },
    collectSpirit() {
      if (this.cultivationData.spirit >= 100) {
        this.cultivationData.spirit -= 100
        this.cultivationData.totalCollected += 100
        this.$Message.success('收取灵气成功！')
      }
    },
    breakthrough() {
      this.$Message.success('突破成功！境界提升至筑基期！')
      this.currentRealm = '筑基期'
      this.nextRealm = '金丹期'
      this.cultivationData.spirit -= 1000
    },
    openAlchemyModal() {
      this.alchemyModalVisible = true
      this.selectedRecipe = null
    },
    openForgingModal() {
      this.forgingModalVisible = true
      this.selectedBlueprint = null
    },

    getGradeColor(grade) {
      const colors = { 'white': '#d9d9d9', 'green': '#19be6b', 'blue': '#2db7f5', 'purple': '#a855f7', 'orange': '#ff9900', 'red': '#ff4d4f' }
      return colors[grade] || '#d9d9d9'
    },
    getGradeTag(item) {
      return item.gradeName || '普通'
    },
    // 复用你原有的选择图纸逻辑，添加洞府加成
    selectRecipe(recipe) {
      this.selectedRecipe = recipe
      this.auxiliaryNums = []
      // 调用你原有的查询详情接口
      this.$http.post('/gameCharaEquip/queryForgingOrPills', {
        charaId: this.getCookie('charaId'),
        packItemIds: recipe.packItemId,
        formulaType: 2
      }).then(res => {
        this.selectedRecipe = { ...recipe, ...res.data.data }
        this.initAuxiliaryMaterials()
      })
    },

    initAuxiliaryMaterials() {
      // 复用你原有的初始化逻辑
      this.formulaMatZiList = []
      if (this.selectedRecipe.matList) {
        this.selectedRecipe.matList.forEach((mat, idx) => {
          if (mat.type === 1) {
            this.auxiliaryNums[idx] = mat.num
          } else {
            this.auxiliaryNums[idx] = Math.min(mat.haveNum, mat.num)
          }
        })
      }
    },

    onAuxiliaryChange(itemId, value, index) {
      // 复用你原有的变更逻辑
      this.$set(this.auxiliaryNums, index, value)
      // 更新 formulaMatZiList...
    },

    async startRefining() {
      // 复用你原有的炼丹逻辑，但添加洞府加成显示
      this.isRefining = true
      // ... 原有代码
      setTimeout(() => {
        this.isRefining = false
        this.$Message.success('炼丹完成！')
      }, 2000)
    },

    // 锻造相关方法 - 复用原有
    selectBlueprint(blueprint) {
      this.selectedBlueprint = blueprint
      // 调用原有查询详情...
    },
    getFacilityIcon(type) {
      const iconMap = {
        'cultivation': 'ios-body',      // 修炼室 - 人物/修炼
        'alchemy': 'ios-flame',         // 炼丹房 - 火焰
        'forging': 'ios-hammer',        // 炼器室 - 锤子
        'spirit': 'ios-flash',          // 灵气
        'default': 'ios-help-circle'    // 默认
      }
      return iconMap[type] || iconMap['default']
    },

    // ========== 设施颜色 ==========
    getFacilityColor(type) {
      const colorMap = {
        'cultivation': '#19be6b',       // 修炼室 - 绿色（生机）
        'alchemy': '#ff6b6b',           // 炼丹房 - 红色（火焰）
        'forging': '#2db7f5',           // 炼器室 - 蓝色（冷静/锻造）
        'spirit': '#ff9900',            // 灵气 - 橙色
        'default': '#999999'            // 默认灰色
      }
      return colorMap[type] || colorMap['default']
    },
    startForging() {
      // 复用你原有的锻造提交逻辑
      // 注意成功率要加上 getFacilityPoints('forging') * 0.05 的加成
    },

    // ========== 新组件事件处理 ==========
    onSpiritCollected() {
      this.checkCaveStatus()
      this.loadBreachInfo()
      this.$emit('exp-updated')
      this.$bus.$emit('caveCollectExp')
    },
    onBreakthrough() {
      this.checkCaveStatus()
      this.loadBreachInfo()
      this.$bus.$emit('caveBreakthrough')
    },
    onAlchemyRefined() {
      this.$emit('user-activity-refresh')
    },
    onAlchemyReceived() {
      this.$emit('user-activity-refresh')
    },
    onForgingDone() {
      this.$emit('user-activity-refresh')
    },
    onSmeltDone() {
      this.$emit('user-activity-refresh')
    },
    onTreasureDone() {
      this.$emit('user-activity-refresh')
    },
    onElementDone() {
      this.$emit('user-activity-refresh')
    },
    onSublimationDone() {
      this.$emit('user-activity-refresh')
    },
    onForgingReceived() {
      this.$emit('user-activity-refresh')
    },
  }
}
</script>

<style  scoped>
.cave-system-container {
  height: 100%;
  position: relative;
}

.create-cave-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.create-cave-overlay .create-cave-panel {
  background: #fff;
  padding: 40px;
  border-radius: 12px;
  text-align: center;
  max-width: 500px;
  width: 90%;
}

.create-cave-overlay .create-cave-panel h2 {
  margin: 20px 0 10px;
  color: #333;
}

.create-cave-overlay .create-cave-panel .desc {
  color: #666;
  margin-bottom: 30px;
}

.create-cave-overlay .create-cave-panel .material-checklist {
  margin: 20px 0;
  text-align: left;
}

.create-cave-overlay .create-cave-panel .material-checklist .material-item {
  display: flex;
  align-items: center;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 10px;
  border: 1px solid #e8e8e8;
}

.create-cave-overlay .create-cave-panel .material-checklist .material-item.sufficient {
  border-color: #19be6b;
  background: #f6ffed;
}

.create-cave-overlay .create-cave-panel .material-checklist .material-item .mat-icon,
.create-cave-overlay .create-cave-panel .material-checklist .material-item .mat-icon-placeholder {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  margin-right: 12px;
  background: #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: #fff;
}

.create-cave-overlay .create-cave-panel .material-checklist .material-item .mat-info {
  flex: 1;
}

.create-cave-overlay .create-cave-panel .material-checklist .material-item .mat-info .mat-name {
  font-weight: bold;
  color: #333;
}

.create-cave-overlay .create-cave-panel .material-checklist .material-item .mat-info .mat-count {
  font-size: 12px;
  color: #ed4014;
  margin-top: 4px;
}

.create-cave-overlay .create-cave-panel .material-checklist .material-item .mat-info .mat-count.enough {
  color: #19be6b;
}

.create-cave-overlay .create-cave-panel .cave-name-input {
  margin: 20px 0;
}

.create-cave-overlay .create-cave-panel .warning-text {
  color: #ed4014;
  font-size: 12px;
  margin-top: 10px;
}

.loading-container {
  height: 600px;
  position: relative;
}

.loading-container .spin-icon-load {
  animation: ani-spin 1s linear infinite;
}

@keyframes ani-spin {
  from { transform: rotate(0deg); }
  50% { transform: rotate(180deg); }
  to { transform: rotate(360deg); }
}

.cave-system-modal /deep/ .ivu-modal-body {
  padding: 0;
  max-height: 80vh;
}

.cave-system-layout {
  display: flex;
  height: 650px;
}

.cave-visual-panel {
  width: 350px;
  background: linear-gradient(180deg, #f8f9fa 0%, #e9ecef 100%);
  border-right: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
}

.cave-visual-panel .quick-actions {
  padding: 15px;
  border-top: 1px solid #e8e8e8;
  background: #fff;
}

.cave-header-bar {
  padding: 12px 15px;
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.cave-header-bar .cave-title-section {
  display: flex;
  align-items: center;
  gap: 10px;
}

.cave-header-bar .cave-title-section .cave-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
}

.cave-header-bar .cave-title-section h3 {
  margin: 0;
  font-size: 16px;
}

.cave-header-bar .cave-title-section .cave-meta {
  margin-top: 4px;
}

.cave-header-bar .cave-title-section .exp-text {
  font-size: 12px;
  color: #999;
  margin-left: 8px;
}

.cave-header-bar .cave-title-section .realm-text {
  font-size: 12px;
  color: #2d8cf0;
  font-weight: bold;
}

.time-wheel-container {
  height: 280px;
  position: relative;
  margin: 13px auto;
  width: 280px;
}

.time-wheel-container .wheel-center {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  cursor: pointer;
  z-index: 10;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

.time-wheel-container .wheel-center .center-content {
  text-align: center;
}

.time-wheel-container .wheel-center .center-content .spirit-value {
  font-size: 20px;
  font-weight: bold;
}

.time-wheel-container .wheel-center .center-content .center-label {
  font-size: 11px;
  opacity: 0.9;
}

.time-wheel-container .wheel-center .center-content .center-sub {
  font-size: 10px;
  opacity: 0.8;
  margin-top: 2px;
}

.time-wheel-container .time-node {
  position: absolute;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  z-index: 5;
}

.time-wheel-container .time-node:hover {
  transform: scale(1.2);
  z-index: 6;
}

.time-wheel-container .time-node.active {
  box-shadow: 0 0 10px currentColor;
}

.time-wheel-container .time-node.cultivation {
  background: rgba(25, 190, 107, 0.1);
  border-color: #19be6b;
  color: #19be6b;
}

.time-wheel-container .time-node.alchemy {
  background: rgba(255, 107, 107, 0.1);
  border-color: #ff6b6b;
  color: #ff6b6b;
}

.time-wheel-container .time-node.forging {
  background: rgba(45, 183, 245, 0.1);
  border-color: #2db7f5;
  color: #2db7f5;
}

.time-wheel-container .time-node .node-content {
  text-align: center;
}

.time-wheel-container .time-node .node-content .earthly-branch {
  font-size: 14px;
  font-weight: bold;
}

.time-wheel-container .time-node .node-content .facility-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  margin: 2px auto 0;
}

.facility-overview {
  flex: 1;
  padding: 10px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.overview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.overview-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #1f2d3d;
}

.slot-summary {
  display: flex;
  align-items: baseline;
  gap: 2px;
  font-size: 14px;
}

.slot-summary .used-slots {
  font-size: 20px;
  font-weight: 700;
  color: #2d8cf0;
}

.slot-summary .separator {
  color: #808695;
  margin: 0 2px;
}

.slot-summary .total-slots {
  font-size: 16px;
  font-weight: 600;
  color: #515a6e;
}

.slot-summary .label {
  font-size: 12px;
  color: #808695;
  margin-left: 4px;
}

.facility-card-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.facility-card-mini {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 1px solid #e8e8e8;
  transition: all 0.2s ease;
  cursor: pointer;
}

.facility-card-mini:hover {
  border-color: #2d8cf0;
  background: #f0f7ff;
}

.facility-card-mini.active {
  border-color: #2d8cf0;
  background: linear-gradient(135deg, rgba(45, 140, 240, 0.05), rgba(255, 255, 255, 0.95));
}

.fac-card-top {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.fac-card-top .fac-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.fac-card-top .fac-name {
  font-weight: 600;
  font-size: 15px;
  color: #1f2d3d;
  flex: 1;
}

.fac-card-top .fac-level-tag {
  font-size: 12px;
  color: #808695;
  background: #f0f0f0;
  padding: 2px 8px;
  border-radius: 10px;
}

.fac-alloc-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.alloc-btn {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 2px solid #2d8cf0;
  background: #edf4ff;
  color: #2d8cf0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  padding: 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
}

.alloc-btn:hover:not(:disabled) {
  background: #2d8cf0;
  color: #fff;
  transform: scale(1.1);
}

.alloc-btn:disabled {
  border-color: #dcdee2 !important;
  background: #f5f5f5 !important;
  color: #dcdee2 !important;
  cursor: not-allowed;
}

.alloc-btn.minus {
  color: #ff6b6b;
  border-color: #ff6b6b;
  background: #fff1f0;
}

.alloc-btn.minus:hover:not(:disabled) {
  background: #ff6b6b;
  border-color: #ff6b6b;
  color: #fff;
}

.alloc-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 50px;
}

.alloc-value {
  font-size: 22px;
  font-weight: 700;
  color: #2d8cf0;
  line-height: 1;
}

.alloc-label {
  font-size: 11px;
  color: #808695;
  margin-top: 2px;
}

.fac-effect-text {
  text-align: center;
  font-size: 12px;
  color: #808695;
}

.fac-effect-text .efficiency {
  color: #19be6b;
  font-weight: 600;
  margin-left: 4px;
}

.overview-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
}

.remaining-hint,
.full-hint,
.unsaved-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #808695;
}

.full-hint {
  color: #19be6b;
}

.unsaved-hint {
  color: #ff9900;
  font-weight: 600;
}

.cave-detail-area {
  flex: 1;
  background: #fff;
  overflow: hidden;
}

.welcome-panel {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.welcome-panel .welcome-content {
  padding: 40px;
}

.welcome-panel .welcome-content h2 {
  margin: 20px 0 10px;
  color: #333;
}

.welcome-panel .welcome-content p {
  color: #999;
  margin-bottom: 30px;
}

.welcome-panel .welcome-content .stats-overview {
  display: flex;
  gap: 40px;
  justify-content: center;
}

.welcome-panel .welcome-content .stats-overview .stat-box {
  padding: 20px 30px;
  background: #f8f9fa;
  border-radius: 12px;
}

.welcome-panel .welcome-content .stats-overview .stat-box .stat-num {
  font-size: 32px;
  font-weight: bold;
  color: #2db7f5;
}

.welcome-panel .welcome-content .stats-overview .stat-box .stat-label {
  color: #666;
  margin-top: 5px;
}

.welcome-panel .welcome-content .cave-tips {
  color: #999;
  font-size: 12px;
  line-height: 1.8;
}

.facility-panel {
  height: 100%;
  overflow: hidden;
}

.facility-panel .panel-header {
  padding: 20px;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 15px;
}

.facility-panel .panel-header .header-title {
  flex: 1;
}

.facility-panel .panel-header .header-title h3 {
  margin: 0;
  font-size: 20px;
}

.facility-panel .panel-header .header-title p {
  margin: 5px 0 0;
  opacity: 0.9;
  font-size: 13px;
}

.facility-panel .facility-actions {
  padding: 20px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  gap: 10px;
}

.facility-panel .facility-preview {
  padding: 20px;
}

.facility-panel .facility-preview h4 {
  margin-bottom: 15px;
  color: #333;
}

.facility-panel .facility-preview .recipe-preview-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.facility-panel .facility-preview .recipe-preview-list .preview-item {
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #e8e8e8;
}

.facility-panel .facility-preview .recipe-preview-list .preview-item.can-make {
  border-color: #19be6b;
  background: #f6ffed;
}

.facility-panel .facility-preview .recipe-preview-list .preview-item .item-icon,
.facility-panel .facility-preview .recipe-preview-list .preview-item .item-icon-placeholder {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: bold;
}

.facility-panel .facility-preview .recipe-preview-list .preview-item .item-icon-placeholder {
  background: #ccc;
}

.facility-panel .facility-preview .recipe-preview-list .preview-item .item-name {
  flex: 1;
  font-size: 14px;
}

.facility-panel .facility-preview .recipe-preview-list .preview-item .item-rate {
  font-size: 12px;
  color: #ff9900;
  font-weight: bold;
}

.cultivation-panel .spirit-card,
.cultivation-panel .exp-card {
  margin: 10px;
}

.current-level-exp {
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 600;
  color: #ff8c00;
}

.cultivation-panel .spirit-card .ivu-card-head,
.cultivation-panel .exp-card .ivu-card-head {
  background: #f8f9fa;
}

.cultivation-panel .spirit-display {
  text-align: center;
}

.cultivation-panel .spirit-display .spirit-circle {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  border: 3px solid #ff9900;
  margin: 0 auto 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: radial-gradient(circle, rgba(255,153,0,0.1) 0%, transparent 70%);
}

.cultivation-panel .spirit-display .spirit-circle .spirit-amount {
  font-size: 32px;
  font-weight: bold;
  color: #ff9900;
}

.cultivation-panel .spirit-display .spirit-circle .spirit-unit {
  font-size: 12px;
  color: #999;
}

.cultivation-panel .spirit-display .spirit-rate {
  color: #666;
  margin-bottom: 15px;
}

.cultivation-panel .hint-text {
  text-align: center;
  color: #999;
  font-size: 12px;
  margin-top: 10px;
}

.cultivation-panel .breakthrough-card {
  margin: 10px;
}

.cultivation-panel .breakthrough-card .breakthrough-content .current-realm {
  font-size: 18px;
  font-weight: bold;
  color: #2db7f5;
  margin-bottom: 15px;
  text-align: center;
}

.cultivation-panel .breakthrough-card .breakthrough-content .breakthrough-require {
  margin-bottom: 20px;
}

.cultivation-panel .breakthrough-card .breakthrough-content .breakthrough-require ul {
  list-style: none;
  margin-top: 10px;
}

.cultivation-panel .breakthrough-card .breakthrough-content .breakthrough-require ul li {
  padding: 5px 0;
  color: #666;
}

.cultivation-panel .breakthrough-card .breakthrough-content .breakthrough-require ul li.met {
  margin-top: 4px;
  color: #19be6b;
  text-decoration: line-through;
}

.inner-modal /deep/ .ivu-modal-body {
  padding: 0;
}

.alchemy-layout-inner,
.forge-layout-inner {
  display: flex;
  height: 600px;
}

.alchemy-layout-inner .alchemy-furnace-area,
.forge-layout-inner .forge-left {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
}

.alchemy-layout-inner .alchemy-recipe-list,
.forge-layout-inner .forge-right {
  width: 300px;
  border-left: 1px solid #e8e8e8;
  background: #f8f9fa;
  padding: 20px;
  overflow-y: auto;
}

.cave-upgrade-content .upgrade-info {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.cave-upgrade-content .upgrade-info h4 {
  margin-bottom: 10px;
  color: #333;
}

.cave-upgrade-content .upgrade-info ul {
  list-style: none;
  padding-left: 0;
}

.cave-upgrade-content .upgrade-info ul li {
  padding: 5px 0;
  color: #666;
}

.cave-upgrade-content .upgrade-info ul li:before {
  content: "✓ ";
  color: #19be6b;
  font-weight: bold;
}

.upgrade-desc-box {
  background: #f8f9fa;
  border-left: 4px solid #2d8cf0;
  color: #515a6e;
  line-height: 1.8;
  padding: 12px 14px;
  border-radius: 6px;
}

.upgrade-desc-box.facility {
  border-left-color: #19be6b;
}

.cave-upgrade-content .upgrade-requirements .req-title {
  font-weight: bold;
  margin-bottom: 10px;
}

.cave-upgrade-content .upgrade-requirements .req-exp {
  margin-bottom: 15px;
}

.cave-upgrade-content .upgrade-requirements .cost-list .cost-item {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.cave-upgrade-content .upgrade-requirements .cost-list .cost-item .insufficient {
  color: #ed4014;
}

.facility-upgrade-content .facility-upgrade-header {
  padding: 30px;
  text-align: center;
  color: #fff;
  margin: -16px -16px 20px -16px;
  border-radius: 4px 4px 0 0;
}

.facility-upgrade-content .facility-upgrade-header h3 {
  margin: 10px 0 5px;
}

.facility-upgrade-content .facility-upgrade-header .level-text {
  opacity: 0.9;
}

.facility-upgrade-content .upgrade-effects {
  margin-bottom: 20px;
}

.facility-upgrade-content .upgrade-effects h4 {
  margin-bottom: 10px;
  color: #333;
}

.facility-upgrade-content .upgrade-effects ul {
  list-style: none;
  background: #f8f9fa;
  padding: 15px;
  border-radius: 6px;
}

.facility-upgrade-content .upgrade-effects ul li {
  padding: 5px 0;
  color: #19be6b;
}

.facility-upgrade-content .upgrade-effects ul li:before {
  content: "↑ ";
}

.facility-upgrade-content .upgrade-materials h4 {
  margin-bottom: 10px;
  color: #333;
}

.facility-upgrade-content .upgrade-materials .material-list .material-row {
  display: flex;
  justify-content: space-between;
  padding: 10px;
  background: #f8f9fa;
  margin-bottom: 8px;
  border-radius: 4px;
}

.facility-upgrade-content .upgrade-materials .material-list .material-row.insufficient {
  background: #fff1f0;
  color: #ed4014;
}

.facility-upgrade-content .no-cost {
  text-align: center;
  padding: 20px;
  color: #999;
}

.furnace-visual {
  text-align: center;
  padding: 40px;
}

.furnace-visual .furnace-img {
  max-width: 200px;
}

.materials-section {
  margin: 20px 0;
}

.materials-section .section-title {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  font-weight: bold;
}

.materials-section .materials-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 10px;
}

.materials-section .materials-grid .material-slot {
  padding: 10px;
  background: #f8f9fa;
  border-radius: 6px;
  text-align: center;
  border: 1px solid #e8e8e8;
}

.materials-section .materials-grid .material-slot.sufficient {
  border-color: #19be6b;
  background: #f6ffed;
}

.materials-section .materials-grid .material-slot .mat-name {
  font-size: 12px;
  margin-bottom: 5px;
}

.materials-section .materials-grid .material-slot .mat-count {
  font-size: 11px;
  color: #666;
  margin-bottom: 5px;
}

.alchemy-action {
  text-align: center;
  padding: 20px;
}

.alchemy-action .success-rate-display {
  font-size: 18px;
  margin-bottom: 15px;
}

.alchemy-action .success-rate-display .bonus-text {
  color: #ff9900;
  font-size: 14px;
  margin-left: 10px;
}

.recipe-item,
.blueprint-item {
  padding: 12px;
  background: #fff;
  border-radius: 6px;
  margin-bottom: 8px;
  cursor: pointer;
  border: 1px solid transparent;
}

.recipe-item:hover,
.blueprint-item:hover,
.recipe-item.active,
.blueprint-item.active {
  border-color: #2db7f5;
  background: #f0f5ff;
}

.recipe-item .recipe-name,
.blueprint-item .blueprint-name {
  font-weight: 500;
  margin-bottom: 4px;
}

.recipe-item .recipe-meta,
.blueprint-item .blueprint-meta {
  font-size: 12px;
  color: #999;
}

.cave-mobile-back {
  padding: 12px 16px 0;
}

.wild-xinfa-cultivation-card {
  margin-top: 16px;
  border-radius: 12px;
  overflow: hidden;
}

.wild-xinfa-cultivation-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.wild-xinfa-empty {
  padding: 14px;
  text-align: center;
  color: #999;
  background: #f8fafc;
  border-radius: 8px;
}

.wild-xinfa-cultivation-list {
  display: grid;
  gap: 12px;
}

.wild-xinfa-cultivation-item {
  padding: 12px;
  border: 1px solid #e8eaec;
  border-radius: 10px;
  background: linear-gradient(135deg, #fffdf5, #f8fbff);
}

.wild-xinfa-cultivation-item.active {
  border-color: #19be6b;
  box-shadow: 0 0 0 2px rgba(25, 190, 107, 0.1);
}

.wild-xinfa-cultivation-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
}

.wild-xinfa-name {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-weight: 600;
}

.wild-xinfa-meta {
  margin-top: 4px;
  color: #808695;
  font-size: 12px;
}

.wild-xinfa-equip-btn {
  margin: 0 0 10px;
}

.wild-xinfa-layer-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  gap: 10px;
}

.wild-xinfa-layer-card {
  padding: 10px;
  border: 1px dashed #dcdee2;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.72);
}

.wild-xinfa-layer-card.unlocked {
  border-style: solid;
  border-color: #19be6b;
}

.wild-xinfa-layer-card.next {
  border-color: #ff9900;
  background: #fffaf0;
}

.layer-card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-weight: 600;
}

.layer-unlock-cost {
  margin: 6px 0 8px;
  font-size: 12px;
  color: #8c6d1f;
  line-height: 1.5;
}

.layer-affix-list {
  display: grid;
  gap: 5px;
  margin-bottom: 8px;
}

.layer-affix {
  font-size: 12px;
  color: #515a6e;
}

.layer-affix.locked {
  color: #999;
}

@media (max-width: 768px) {
  .cave-system-layout {
    display: block;
    height: auto;
    min-height: 0;
  }

  .cave-visual-panel,
  .cave-detail-area {
    width: 100%;
    min-width: 0;
  }

  .cave-visual-panel {
    border-right: none;
  }

  .facility-panel .facility-actions {
    flex-wrap: wrap;
  }

  .facility-panel .facility-actions .ivu-btn {
    flex: 1 1 calc(50% - 5px);
  }

  .facility-panel .facility-preview .recipe-preview-list {
    grid-template-columns: 1fr;
  }

  .cultivation-content .ivu-row {
    display: block;
  }

  .cultivation-content .ivu-col {
    width: 100%;
    max-width: 100%;
    display: block;
  }

  .wild-xinfa-layer-grid {
    grid-template-columns: 1fr;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/CaveSystem.vue